import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSync, 
  faFileInvoice, 
  faChartBar, 
  faShieldAlt,
  faCog,
  faArrowRight,
  faCheck,
  faClock
} from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(ScrollTrigger);

const FeaturesSection = ({ onDemoClick }) => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const [hoveredCard, setHoveredCard] = useState(null);

  const features = [
    {
      icon: faSync,
      title: '订单同步',
      description: '实时同步物流订单数据，自动识别收入成本',
      details: [
        '多平台订单自动抓取',
        '智能数据清洗与分类',
        '实时状态更新推送'
      ],
      process: [
        { step: '订单抓取', icon: faSync },
        { step: '数据处理', icon: faCog },
        { step: '凭证生成', icon: faFileInvoice }
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: faFileInvoice,
      title: '凭证生成',
      description: '智能生成会计凭证，符合财务规范要求',
      details: [
        '自动科目匹配',
        '批量凭证生成',
        '审核流程管理'
      ],
      process: [
        { step: '规则配置', icon: faCog },
        { step: '自动生成', icon: faFileInvoice },
        { step: '审核确认', icon: faCheck }
      ],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: faChartBar,
      title: '财务报表',
      description: '多维度财务分析，实时掌握经营状况',
      details: [
        '实时财务报表',
        '多维度数据分析',
        '可视化图表展示'
      ],
      process: [
        { step: '数据汇总', icon: faChartBar },
        { step: '报表生成', icon: faFileInvoice },
        { step: '分析展示', icon: faChartBar }
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: faShieldAlt,
      title: '安全保障',
      description: '银行级安全防护，保障数据安全可靠',
      details: [
        '数据加密存储',
        '权限分级管理',
        '操作日志记录'
      ],
      process: [
        { step: '身份验证', icon: faShieldAlt },
        { step: '权限控制', icon: faCog },
        { step: '安全审计', icon: faCheck }
      ],
      color: 'from-red-500 to-orange-500'
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    
    // 卡片入场动画
    gsap.fromTo(
      cardsRef.current,
      { y: 100, opacity: 0, scale: 0.8 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: section,
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleCardHover = (index) => {
    setHoveredCard(index);
    
    // 卡片悬停动画
    gsap.to(cardsRef.current[index], {
      scale: 1.05,
      y: -10,
      duration: 0.3,
      ease: 'power2.out'
    });
  };

  const handleCardLeave = (index) => {
    setHoveredCard(null);
    
    gsap.to(cardsRef.current[index], {
      scale: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out'
    });
  };

  return (
    <section ref={sectionRef} className="py-20 bg-background-light">
      <div className="container mx-auto px-4">
        {/* 标题 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
            核心功能特性
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            专为物流快运行业设计的智能财务功能，让财务管理变得简单高效
          </p>
        </div>

        {/* 功能卡片网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              ref={el => cardsRef.current[index] = el}
              className="bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transition-all duration-300"
              onMouseEnter={() => handleCardHover(index)}
              onMouseLeave={() => handleCardLeave(index)}
            >
              {/* 卡片头部 */}
              <div className={`bg-gradient-to-r ${feature.color} p-6 text-white`}>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={feature.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-white/90 text-sm">{feature.description}</p>
              </div>

              {/* 卡片内容 */}
              <div className="p-6">
                {/* 功能详情 */}
                <ul className="space-y-2 mb-6">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                      <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2 text-xs" />
                      {detail}
                    </li>
                  ))}
                </ul>

                {/* 流程图 */}
                <div className={`transition-all duration-300 ${
                  hoveredCard === index ? 'opacity-100 max-h-32' : 'opacity-0 max-h-0'
                } overflow-hidden`}>
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">处理流程</h4>
                    <div className="flex items-center justify-between">
                      {feature.process.map((step, stepIndex) => (
                        <React.Fragment key={stepIndex}>
                          <div className="flex flex-col items-center">
                            <div className={`w-8 h-8 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mb-1`}>
                              <FontAwesomeIcon icon={step.icon} className="text-white text-xs" />
                            </div>
                            <span className="text-xs text-gray-600 text-center">{step.step}</span>
                          </div>
                          {stepIndex < feature.process.length - 1 && (
                            <FontAwesomeIcon 
                              icon={faArrowRight} 
                              className="text-gray-400 text-xs mx-1" 
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 底部CTA */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold gradient-text mb-4">
              想了解更多功能特性？
            </h3>
            <p className="text-gray-600 mb-6">
              我们的产品专家将为您详细介绍系统功能，并根据您的业务需求提供定制化解决方案
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={onDemoClick}
                className="btn-primary"
              >
                预约产品演示
              </button>
              <button 
                onClick={() => window.open('https://ucnglu8dvh19.feishu.cn/wiki/IxCqw3YyoiEXZqktsQjc1k5Vnhe', '_blank')}
                className="border-2 border-primary-blue text-primary-blue px-6 py-3 rounded-lg font-semibold hover:bg-primary-blue hover:text-white transition-all duration-300"
              >
                查看产品手册
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;